import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Package, FileText, LayoutList, Upload, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, imgSrc } from "@/lib/api";

const EMPTY_PRODUCT = { name: "", description: "", price_inr: "", original_price_inr: "", price_usd: "", original_price_usd: "", shipping_inr: "", shipping_usd: "", image: "", tag: "", active: true };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [content, setContent] = useState(null);
  const [editing, setEditing] = useState(null); // product being edited or EMPTY_PRODUCT
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(null); // expanded order id
  const [showcase, setShowcase] = useState([]);
  const [secType, setSecType] = useState("topseller");
  const [editingItem, setEditingItem] = useState(null); // showcase item being edited

  const SECTION_TYPES = [
    { key: "topseller", label: "Top Sellers" },
    { key: "celebrity", label: "Celebrities" },
    { key: "diwali_day", label: "Diwali Days" },
    { key: "value_point", label: "Trust Points" },
  ];

  const saveItem = async () => {
    try {
      if (editingItem.id) await api.put(`/admin/showcase/${editingItem.id}`, editingItem);
      else await api.post("/admin/showcase", editingItem);
      toast.success("Saved — live on site within seconds");
      setEditingItem(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this item from the website?")) return;
    try {
      await api.delete(`/admin/showcase/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const uploadItemImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setEditingItem((prev) => ({ ...prev, image: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const exportCSV = () => {
    const head = ["Order ID", "Date", "Customer", "Email", "Phone", "Profession", "Address", "Message", "Items", "Subtotal", "Shipping", "Total", "Currency", "Status", "Payment ID", "Razorpay Order ID"];
    const rows = orders.map((o) => [
      o.order_id, new Date(o.created_at).toLocaleString("en-IN"),
      o.customer?.name, o.customer?.email, o.customer?.phone, o.customer?.profession,
      (o.customer?.address || "").replace(/[\r\n]+/g, " "), (o.customer?.message || "").replace(/[\r\n]+/g, " "),
      (o.items || []).map((i) => `${i.name} x${i.quantity}`).join("; "),
      o.subtotal, o.shipping, o.total, o.currency, o.status,
      o.payment_id || "", o.razorpay_order_id || "",
    ]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv" }));
    a.download = `rudralife-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const resetCountdown = async () => {
    try {
      const end = new Date(Date.now() + 24 * 3600 * 1000).toISOString();
      await api.put("/admin/content", { countdown_end: end });
      setContent((prev) => ({ ...prev, countdown_end: end }));
      toast.success("Countdown restarted — 24 hours from now");
    } catch {
      toast.error("Could not reset countdown");
    }
  };

  const authErr = useCallback((e) => {
    if (e?.response?.status === 401) {
      localStorage.removeItem("rl_admin_token");
      navigate("/admin");
    }
  }, [navigate]);

  const load = useCallback(() => {
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(authErr);
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(authErr);
    api.get("/admin/showcase").then((r) => setShowcase(r.data)).catch(authErr);
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, [authErr]);

  useEffect(() => {
    if (!localStorage.getItem("rl_admin_token")) return navigate("/admin");
    load();
  }, [navigate, load]);

  const saveProduct = async () => {
    try {
      const num = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));
      const payload = {
        ...editing,
        price_inr: parseFloat(editing.price_inr),
        original_price_inr: num(editing.original_price_inr),
        price_usd: num(editing.price_usd),
        original_price_usd: num(editing.original_price_usd),
        shipping_inr: num(editing.shipping_inr),
        shipping_usd: num(editing.shipping_usd),
      };
      if (editing.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Product saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const uploadContentImage = async (field, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      const updated = { ...content, [field]: data.url };
      await api.put("/admin/content", updated);  // auto-save — live within seconds
      setContent(updated);
      toast.success("Image uploaded & live on site");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setEditing((prev) => ({ ...prev, image: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveContent = async () => {
    try {
      await api.put("/admin/content", content);
      toast.success("Site content updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const TABS = [
    { key: "orders", label: "Orders", icon: LayoutList },
    { key: "products", label: "Products", icon: Package },
    { key: "sections", label: "Sections", icon: LayoutList },
    { key: "content", label: "Site Content", icon: FileText },
  ];

  const inputCls = "w-full rounded-lg border border-[#D4AF37]/25 bg-[#FDF8F0] px-4 py-2.5 text-sm text-[#2B1E12] outline-none focus:border-[#D4AF37]";

  return (
    <div className="min-h-screen bg-[#FDF8F0] text-[#2B1E12]" data-testid="admin-dashboard">
      <header className="flex items-center justify-between border-b border-[#D4AF37]/15 px-5 py-4 sm:px-8">
        <div className="flex items-center gap-3">
          <img src="/assets/brand/logo.png" alt="Rudralife" className="h-8 w-auto" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#8F6E1D]">Admin</span>
        </div>
        <button data-testid="admin-logout" onClick={() => { localStorage.removeItem("rl_admin_token"); navigate("/admin"); }}
          className="flex items-center gap-2 rounded-full border border-[#D4AF37]/30 px-4 py-2 text-xs text-[#6B5B45] transition-colors hover:bg-[#D4AF37]/10 hover:text-[#8F6E1D]">
          <LogOut className="h-3.5 w-3.5" /> Logout
        </button>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
        <div className="mb-8 flex gap-2">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${tab === key ? "bg-[#D4AF37] text-[#1C130B]" : "border border-[#D4AF37]/25 text-[#6B5B45] hover:text-[#8F6E1D]"}`}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <div data-testid="admin-orders">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-[#6B5B45]">{orders.length} order{orders.length !== 1 ? "s" : ""} · click a row for full customer details</p>
              <button data-testid="orders-export" onClick={exportCSV}
                className="flex items-center gap-2 rounded-full bg-[#1C130B] px-5 py-2.5 text-[11px] font-bold uppercase tracking-wider text-[#F3E5AB] transition-colors hover:bg-[#8F6E1D]">
                <Upload className="h-3.5 w-3.5 rotate-180" /> Download Excel (CSV)
              </button>
            </div>
            <div className="overflow-x-auto rounded-xl border border-[#D4AF37]/20">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-[#FBF6EA] text-xs uppercase tracking-wider text-[#8F6E1D]">
                <tr>
                  <th className="px-5 py-3.5">Order ID</th><th className="px-5 py-3.5">Customer</th>
                  <th className="px-5 py-3.5">Items</th><th className="px-5 py-3.5">Total</th>
                  <th className="px-5 py-3.5">Status</th><th className="px-5 py-3.5">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#D4AF37]/10">
                {orders.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-[#6B5B45]">No orders yet</td></tr>}
                {orders.map((o) => (
                  <React.Fragment key={o.order_id}>
                  <tr data-testid={`admin-order-${o.order_id}`} onClick={() => setExpanded(expanded === o.order_id ? null : o.order_id)}
                    className="cursor-pointer text-[#6B5B45] transition-colors hover:bg-[#FBF6EA]">
                    <td className="px-5 py-3.5 font-mono text-xs text-[#8F6E1D]">{o.order_id}</td>
                    <td className="px-5 py-3.5">{o.customer?.name}<br /><span className="text-xs opacity-70">{o.customer?.email} · {o.customer?.phone}</span></td>
                    <td className="px-5 py-3.5">{o.items?.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</td>
                    <td className="px-5 py-3.5 font-semibold text-[#2B1E12]">{o.currency === "USD" ? "$" : "₹"}{Number(o.total).toLocaleString()}</td>
                    <td className="px-5 py-3.5">
                      <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${o.status === "paid" ? "bg-[#D4AF37]/15 text-[#8F6E1D]" : o.status === "failed" ? "bg-red-500/15 text-red-500" : "bg-[#E67300]/15 text-[#E67300]"}`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-xs">{new Date(o.created_at).toLocaleString("en-IN")}</td>
                  </tr>
                  {expanded === o.order_id && (
                    <tr data-testid={`admin-order-detail-${o.order_id}`}>
                      <td colSpan={6} className="bg-[#FBF6EA] px-6 py-5">
                        <div className="grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
                          <div><p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#8F6E1D]">Delivery Address</p><p className="text-[#2B1E12]">{o.customer?.address}</p></div>
                          <div><p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#8F6E1D]">Profession</p><p className="text-[#2B1E12]">{o.customer?.profession || "—"}</p></div>
                          <div><p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#8F6E1D]">Message</p><p className="text-[#2B1E12]">{o.customer?.message || "—"}</p></div>
                          <div><p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#8F6E1D]">Shipping</p><p className="text-[#2B1E12]">{o.currency === "USD" ? `$${o.shipping} (international)` : o.shipping === 0 ? "FREE (India)" : `₹${o.shipping}`}</p></div>
                          <div><p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#8F6E1D]">Payment ID</p>
                            {o.payment_id ? (
                              <a href={`https://dashboard.razorpay.com/app/payments/${o.payment_id}`} target="_blank" rel="noreferrer" className="font-mono text-xs text-[#8F6E1D] underline">{o.payment_id}</a>
                            ) : <p className="text-[#2B1E12]">—</p>}
                          </div>
                          <div><p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#8F6E1D]">Razorpay Order</p><p className="font-mono text-xs text-[#2B1E12]">{o.razorpay_order_id || "—"}</p></div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {tab === "products" && (
          <div data-testid="admin-products">
            {editing ? (
              <div className="rounded-xl border border-[#D4AF37]/25 bg-white p-7">
                <h3 className="mb-6 font-display text-xl font-bold">{editing.id ? "Edit Product" : "New Product"}</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">Name</label>
                    <input data-testid="product-form-name" className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
                  <div><label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">Selling Price ₹ (India)</label>
                    <input data-testid="product-form-price" type="number" className={inputCls} value={editing.price_inr} onChange={(e) => setEditing({ ...editing, price_inr: e.target.value })} /></div>
                  <div><label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">Before-Discount ₹ (shown cut)</label>
                    <input data-testid="product-form-original-inr" type="number" placeholder="Optional" className={inputCls} value={editing.original_price_inr ?? ""} onChange={(e) => setEditing({ ...editing, original_price_inr: e.target.value })} /></div>
                  <div><label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">Selling Price $ (International)</label>
                    <input data-testid="product-form-price-usd" type="number" placeholder="Auto: ₹ ÷ 75" className={inputCls} value={editing.price_usd ?? ""} onChange={(e) => setEditing({ ...editing, price_usd: e.target.value })} /></div>
                  <div><label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">Before-Discount $ (shown cut)</label>
                    <input data-testid="product-form-original-usd" type="number" placeholder="Optional" className={inputCls} value={editing.original_price_usd ?? ""} onChange={(e) => setEditing({ ...editing, original_price_usd: e.target.value })} /></div>
                  <div><label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">Shipping ₹ India (blank = FREE)</label>
                    <input data-testid="product-form-shipping-inr" type="number" min="0" placeholder="Default: FREE (0)" className={inputCls} value={editing.shipping_inr ?? ""} onChange={(e) => setEditing({ ...editing, shipping_inr: e.target.value })} /></div>
                  <div><label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">Shipping $ International (blank = $40)</label>
                    <input data-testid="product-form-shipping-usd" type="number" min="0" placeholder="Default: 40" className={inputCls} value={editing.shipping_usd ?? ""} onChange={(e) => setEditing({ ...editing, shipping_usd: e.target.value })} /></div>
                  <div className="sm:col-span-2"><label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">Description</label>
                    <textarea data-testid="product-form-description" rows={3} className={inputCls} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
                  <div><label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">Tag (e.g. Bestseller)</label>
                    <input data-testid="product-form-tag" className={inputCls} value={editing.tag} onChange={(e) => setEditing({ ...editing, tag: e.target.value })} /></div>
                  <div className="flex items-end gap-3 pb-1">
                    <label className="flex items-center gap-2 text-sm text-[#6B5B45]">
                      <input data-testid="product-form-active" type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="h-4 w-4 accent-[#D4AF37]" />
                      Visible on store
                    </label>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">Product Image</label>
                    <div className="flex items-center gap-4">
                      {editing.image && <img src={imgSrc(editing.image)} alt="" className="h-16 w-16 rounded-lg bg-[#F7F0DF] object-contain p-1" />}
                      <label data-testid="product-form-upload" className="flex cursor-pointer items-center gap-2 rounded-full border border-[#D4AF37]/35 px-5 py-2.5 text-xs font-semibold text-[#8F6E1D] transition-colors hover:bg-[#D4AF37]/10">
                        <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload Image"}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0])} />
                      </label>
                    </div>
                  </div>
                </div>
                <div className="mt-7 flex gap-3">
                  <button data-testid="product-form-save" onClick={saveProduct}
                    className="rounded-full bg-[#D4AF37] px-7 py-3 text-xs font-bold uppercase tracking-widest text-[#1C130B] transition-colors hover:bg-[#F3E5AB]">Save Product</button>
                  <button data-testid="product-form-cancel" onClick={() => setEditing(null)}
                    className="rounded-full border border-[#D4AF37]/30 px-7 py-3 text-xs font-semibold uppercase tracking-widest text-[#6B5B45] transition-colors hover:text-[#8F6E1D]">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <button data-testid="product-add" onClick={() => setEditing({ ...EMPTY_PRODUCT })}
                  className="mb-6 flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-[#1C130B] transition-colors hover:bg-[#F3E5AB]">
                  <Plus className="h-3.5 w-3.5" /> Add Product
                </button>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {products.map((p) => (
                    <div key={p.id} data-testid={`admin-product-${p.slug || p.id}`} className="flex gap-4 rounded-xl border border-[#D4AF37]/20 bg-white p-4">
                      <img src={imgSrc(p.image)} alt={p.name} className="h-16 w-16 rounded-lg bg-[#F7F0DF] object-contain p-1" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{p.name}</p>
                        <p className="text-xs text-[#8F6E1D]">₹{Number(p.price_inr).toLocaleString("en-IN")}</p>
                        <p className="text-[10px] uppercase tracking-wider text-[#6B5B45]">{p.active ? "Visible" : "Hidden"}</p>
                      </div>
                      <button data-testid={`admin-edit-${p.slug || p.id}`} onClick={() => setEditing(p)} aria-label="Edit"
                        className="self-start text-[#6B5B45] transition-colors hover:text-[#8F6E1D]"><Pencil className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "sections" && (
          <div data-testid="admin-sections">
            <div className="mb-6 flex flex-wrap gap-2">
              {SECTION_TYPES.map((t) => (
                <button key={t.key} data-testid={`section-type-${t.key}`} onClick={() => { setSecType(t.key); setEditingItem(null); }}
                  className={`rounded-full px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${secType === t.key ? "bg-[#1C130B] text-[#F3E5AB]" : "border border-[#D4AF37]/25 text-[#6B5B45] hover:text-[#8F6E1D]"}`}>
                  {t.label}
                </button>
              ))}
              <button data-testid="section-add" onClick={() => setEditingItem({ type: secType, name: "", text: "", image: "", order: showcase.filter((s) => s.type === secType).length, active: true })}
                className="flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2 text-xs font-bold uppercase tracking-wider text-[#1C130B] transition-colors hover:bg-[#F3E5AB]">
                <Plus className="h-3.5 w-3.5" /> Add New
              </button>
            </div>

            {editingItem ? (
              <div className="rounded-xl border border-[#D4AF37]/25 bg-white p-7">
                <h3 className="mb-6 font-display text-xl font-semibold text-[#2B1E12]">{editingItem.id ? "Edit" : "New"} — {SECTION_TYPES.find((t) => t.key === secType)?.label}</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">Name / Title</label>
                    <input data-testid="section-form-name" className={inputCls} value={editingItem.name} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} /></div>
                  <div><label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">Order (position)</label>
                    <input data-testid="section-form-order" type="number" className={inputCls} value={editingItem.order} onChange={(e) => setEditingItem({ ...editingItem, order: parseInt(e.target.value) || 0 })} /></div>
                  <div className="sm:col-span-2"><label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">{secType === "celebrity" ? "Profession (e.g. Actor, Actress, Model)" : "Text / One-liner"}</label>
                    <textarea data-testid="section-form-text" rows={2} className={inputCls} value={editingItem.text} onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })} /></div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">Image</label>
                    <div className="flex items-center gap-4">
                      {editingItem.image && <img src={imgSrc(editingItem.image)} alt="" className="h-16 w-16 rounded-lg bg-[#F7F0DF] object-contain p-1" />}
                      <label data-testid="section-form-upload" className="flex cursor-pointer items-center gap-2 rounded-full border border-[#B08D2B]/35 px-5 py-2.5 text-xs font-semibold text-[#8F6E1D] transition-colors hover:bg-[#D4AF37]/15">
                        <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload Image"}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadItemImage(e.target.files[0])} />
                      </label>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-[#6B5B45]">
                    <input data-testid="section-form-active" type="checkbox" checked={editingItem.active} onChange={(e) => setEditingItem({ ...editingItem, active: e.target.checked })} className="h-4 w-4 accent-[#B08D2B]" />
                    Visible on site
                  </label>
                </div>
                <div className="mt-7 flex gap-3">
                  <button data-testid="section-form-save" onClick={saveItem}
                    className="rounded-full bg-[#D4AF37] px-7 py-3 text-xs font-bold uppercase tracking-widest text-[#1C130B] transition-colors hover:bg-[#F3E5AB]">Save</button>
                  <button data-testid="section-form-cancel" onClick={() => setEditingItem(null)}
                    className="rounded-full border border-[#D4AF37]/30 px-7 py-3 text-xs font-semibold uppercase tracking-widest text-[#6B5B45] transition-colors hover:text-[#8F6E1D]">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {showcase.filter((s) => s.type === secType).map((s) => (
                  <div key={s.id} data-testid={`section-item-${s.id.slice(0, 8)}`} className="flex gap-4 rounded-xl border border-[#D4AF37]/20 bg-white p-4">
                    {s.image && <img src={imgSrc(s.image)} alt={s.name} className="h-16 w-16 rounded-lg bg-[#F7F0DF] object-contain p-1" />}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-[#2B1E12]">{s.name}</p>
                      {s.text && <p className="line-clamp-1 text-xs text-[#6B5B45]">{s.text}</p>}
                      <p className="text-[10px] uppercase tracking-wider text-[#6B5B45]">{s.active ? "Visible" : "Hidden"} · #{s.order}</p>
                    </div>
                    <div className="flex flex-col gap-2 self-start">
                      <button data-testid={`section-edit-${s.id.slice(0, 8)}`} onClick={() => setEditingItem(s)} aria-label="Edit"
                        className="text-[#6B5B45] transition-colors hover:text-[#8F6E1D]"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`section-delete-${s.id.slice(0, 8)}`} onClick={() => deleteItem(s.id)} aria-label="Delete"
                        className="text-[#6B5B45] transition-colors hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "content" && content && (
          <div data-testid="admin-content" className="rounded-xl border border-[#D4AF37]/25 bg-white p-7">
            <div className="grid gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-md border border-[#E9DFC8] p-4">
                  <label className="mb-2 block text-xs uppercase tracking-wider text-[#6B5B45]">Diwali Countdown</label>
                  <p className="mb-3 text-xs text-[#6B5B45]">
                    Ends: <span className="font-semibold text-[#2B1E12]" data-testid="countdown-end-display">{content.countdown_end ? new Date(content.countdown_end).toLocaleString("en-IN") : "—"}</span>
                  </p>
                  <button data-testid="countdown-reset" onClick={resetCountdown}
                    className="rounded-full bg-[#D4AF37] px-5 py-2.5 text-[11px] font-bold uppercase tracking-wider text-[#1C130B] transition-colors hover:bg-[#8F6E1D] hover:text-white">
                    Restart 24-Hour Countdown
                  </button>
                </div>
                <div className="rounded-md border border-[#E9DFC8] p-4">
                  <label className="mb-2 block text-xs uppercase tracking-wider text-[#6B5B45]">Auto-Scroll Speed</label>
                  <select
                    data-testid="scroll-speed-select"
                    value={content.scroll_speed || 1}
                    onChange={(e) => setContent({ ...content, scroll_speed: parseFloat(e.target.value) })}
                    className="w-full rounded-lg border border-[#D4AF37]/25 bg-white px-4 py-2.5 text-sm text-[#2B1E12] outline-none focus:border-[#B08D2B]"
                  >
                    {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}x{ v === 1 ? " (default)" : ""}</option>)}
                  </select>
                  <p className="mt-2 text-xs text-[#6B5B45]">Applies to all scrolling sections. Save Content to apply.</p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {[["logo", "Site Logo"], ["hero_image", "Desktop Banner 1"], ["hero_image_2", "Desktop Banner 2"], ["hero_image_mobile", "Mobile Banner 1"], ["hero_image_mobile_2", "Mobile Banner 2"], ["anniversary_image_left", "Anniversary Image (Left)"], ["anniversary_image_right", "Anniversary Image (Right)"], ["gallery_1", "Gallery Photo 1 (tall)"], ["gallery_2", "Gallery Photo 2"], ["gallery_3", "Gallery Photo 3"], ["gallery_4", "Gallery Photo 4 (tall)"], ["gallery_5", "Gallery Photo 5"], ["gallery_6", "Gallery Photo 6"]].map(([field, label]) => (
                  <div key={field} className="rounded-md border border-[#E9DFC8] p-4">
                    <label className="mb-2 block text-xs uppercase tracking-wider text-[#6B5B45]">{label}</label>
                    <div className="flex items-center gap-4">
                      {content[field] ? (
                        <img src={imgSrc(content[field])} alt={label} className={field === "logo" ? "h-10 w-auto bg-[#F7F0DF] p-1" : "h-16 w-32 rounded-md object-cover"} />
                      ) : (
                        <span className="text-xs italic text-[#6B5B45]">Not set — desktop banner will be used</span>
                      )}
                      <label data-testid={`content-upload-${field}`} className="flex cursor-pointer items-center gap-2 rounded-full border border-[#B08D2B]/35 px-5 py-2.5 text-xs font-semibold text-[#8F6E1D] transition-colors hover:bg-[#D4AF37]/15">
                        <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Change Image"}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadContentImage(field, e.target.files[0])} />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              {[
                ["announcement", "Announcement Banner"], ["hero_headline", "Hero Headline"], ["hero_subtext", "Hero Subtext"],
                ["countdown_title", "Countdown Title (Happy Diwali)"], ["countdown_subtext", "Countdown Text"],
                ["intro_title", "Intro Title"], ["intro_text", "Intro Text"],
                ["collection_eyebrow", "Collection Eyebrow"], ["collection_title", "Collection Title"], ["collection_accent", "Collection Gold Word"], ["collection_subtext", "Collection Subtext"],
                ["anniversary_headline", "Anniversary Headline"], ["anniversary_text", "Anniversary Text"],
                ["topselling_eyebrow", "Top-Selling Eyebrow"], ["topselling_title", "Top-Selling Title"], ["topselling_accent", "Top-Selling Gold Word"], ["topselling_subtext", "Top-Selling Subtext"],
                ["gallery_eyebrow", "Gallery Eyebrow"], ["gallery_title", "Gallery Title"], ["gallery_accent", "Gallery Gold Word"], ["gallery_subtext", "Gallery Subtext"],
                ["days_eyebrow", "Diwali Days Eyebrow"], ["days_title", "Diwali Days Title"], ["days_accent", "Diwali Days Gold Word"],
                ["value_eyebrow", "Trust Section Eyebrow"], ["value_title", "Trust Section Title"], ["value_text", "Trust Section Text"],
                ["celebrities_eyebrow", "Celebrities Eyebrow"], ["celebrities_title", "Celebrities Title"], ["celebrities_accent", "Celebrities Gold Word"], ["celebrities_subtext", "Celebrities Subtext"],
                ["press_label", "Press Label (As Featured In)"],
                ["contact_phone", "Contact Phone"], ["contact_address", "Contact Address"],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="mb-1 block text-xs uppercase tracking-wider text-[#6B5B45]">{label}</label>
                  {["intro_text", "anniversary_text", "value_text", "contact_address", "countdown_subtext", "collection_subtext", "gallery_subtext", "topselling_subtext", "celebrities_subtext"].includes(key) ? (
                    <textarea data-testid={`content-${key}`} rows={3} className={inputCls} value={content[key] || ""} onChange={(e) => setContent({ ...content, [key]: e.target.value })} />
                  ) : (
                    <input data-testid={`content-${key}`} className={inputCls} value={content[key] || ""} onChange={(e) => setContent({ ...content, [key]: e.target.value })} />
                  )}
                </div>
              ))}
            </div>
            <button data-testid="content-save" onClick={saveContent}
              className="mt-7 rounded-full bg-[#D4AF37] px-7 py-3 text-xs font-bold uppercase tracking-widest text-[#1C130B] transition-colors hover:bg-[#F3E5AB]">
              Save Content
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
